/**
 * KAPITEL-PUNKTE — welche Punkte die Leiste rechts zeigt (Jans Wunsch
 * 06.10.2026, nach dem Vorbild der Leiste aus Scrolly 1.0, z. B.
 * scrlly.com/stories/knie-op: große Punkte für Kapitel, kleine für die Slides
 * des aktuellen Kapitels).
 *
 * Regeln:
 * - Der TITEL-Slide (Index 0) hat einen eigenen Punkt und beginnt Kapitel 1
 *   (Jans Korrektur 17.10.2026: „Es muss einen Dot für den Titelslide geben,
 *   zu dem man wieder zurück zum Anfang springen kann“). Bis dahin galt die
 *   1.0-Regel „Startbildschirm ohne Punkt, Kapitel 1 beginnt danach“.
 * - Ein weiteres Kapitel beginnt an jeder Slide ab Index 2 mit `isChapter`;
 *   die erste Slide nach dem Titel gehört immer zu Kapitel 1 (ältere Scrollys
 *   hatten dort den Kapitel-Haken für den alten Kapitelanfang).
 * - Gibt es so nur EIN Kapitel, ist jede Slide ihr eigenes Kapitel (dann eben
 *   ein Punkt je Slide, ohne Gruppen) — der Titel inklusive.
 *
 * Reine Funktionen — die Darstellung steht in KapitelPunkte.tsx (eigener
 * Dateiname, weil macOS Groß-/Kleinschreibung nicht unterscheidet).
 */

export interface KapitelSlide {
    isChapter?: boolean | null;
    is_chapter?: boolean | null;
    /** Pro-Editor „Name am Punkt“ */
    chapterLabel?: string | null;
}

export interface KapitelGruppe {
    /** Slide-Indizes (in der Slide-Liste des Scrollys), der erste ist der Kapitelanfang */
    slides: number[];
    /** true, wenn wirklich nach Kapiteln gruppiert wird (sonst ein Punkt je Slide) */
    istKapitel: boolean;
}

const istKapitelanfang = (s: KapitelSlide | null | undefined) => !!(s?.isChapter ?? s?.is_chapter);

export function kapitelGruppen(slides: ReadonlyArray<KapitelSlide | null | undefined>): KapitelGruppe[] {
    const gruppen: number[][] = [];
    for (let i = 0; i < slides.length; i++) {
        if (gruppen.length === 0 || (i >= 2 && istKapitelanfang(slides[i]))) gruppen.push([i]);
        else gruppen[gruppen.length - 1].push(i);
    }
    if (gruppen.length <= 1) {
        return (gruppen[0] ?? []).map((i) => ({ slides: [i], istKapitel: false }));
    }
    return gruppen.map((g) => ({ slides: g, istKapitel: true }));
}

/**
 * Name am Kapitel-Punkt: das Feld „Name am Punkt“ der Kapitel-Slide — beim
 * ersten Kapitel (Titel) steht er an der ersten Slide NACH dem Titel (dessen
 * Menü im Pro-Editor hat keine Kapitel-Zeile; der Scene-Editor speichert
 * dort ebenso). Interne Slide-Namen
 * („new slide“, „Hintergrund v2“) erscheinen nie (Jans Wahl 06.10.2026);
 * ohne Namen undefined.
 */
export function kapitelName(
    slides: ReadonlyArray<KapitelSlide | null | undefined>,
    anfang: number,
): string | undefined {
    const traeger = anfang === 0 ? 1 : anfang;
    return (slides[traeger]?.chapterLabel ?? "").trim() || undefined;
}

/**
 * Für den Pro-Editor: beginnt mit Slide `index` ein Kapitel-Punkt (dann hat
 * sie ein Feld „Name am Punkt“), und welche Nummer zeigt er ohne Namen?
 * null, wenn nicht — auch wenn der Scrolly gar keine Kapitel-Punkte hat.
 */
export function kapitelPunktNummer(
    slides: ReadonlyArray<KapitelSlide | null | undefined>,
    index: number,
): number | null {
    const gruppen = kapitelGruppen(slides);
    // Kapitel 1 beginnt am Titel, sein Name steht an der Slide danach — auch
    // ohne weitere Kapitel: dann trägt der Titel-Punkt ihn (Jan 20.10.2026)
    if (index === 0) return null;
    if (index === 1) return slides.length > 1 ? 1 : null;
    if (!gruppen[0]?.istKapitel) return null;
    const k = gruppen.findIndex((g) => g.slides[0] === index);
    return k >= 0 ? k + 1 : null;
}

/** Wo der Leser steht: Kapitel und Position darin; null vor der ersten Slide nach dem Start. */
export function kapitelPosition(
    gruppen: ReadonlyArray<KapitelGruppe>,
    aktuellerSlide: number,
): { kapitel: number; slide: number } | null {
    for (let k = 0; k < gruppen.length; k++) {
        const s = gruppen[k].slides.indexOf(aktuellerSlide);
        if (s >= 0) return { kapitel: k, slide: s };
    }
    return null;
}

/**
 * Maße der Leiste; `eng` für lange Scrollys, die sonst nicht passen.
 * `zuwachs`: um so viel ist der Tropfen (wo der Leser steht) größer als sein
 * Punkt. Abstand 12 statt 8 seit Jans Blick am 06.10.2026 („ein kleines
 * bisschen mehr auseinander“).
 */
export const PUNKT_MASSE = {
    normal: { gross: 10, klein: 6, abstand: 12, rand: 8, zuwachs: 3 },
    eng: { gross: 8, klein: 5, abstand: 8, rand: 6, zuwachs: 2 },
} as const;

export type PunktMasse = (typeof PUNKT_MASSE)[keyof typeof PUNKT_MASSE];

/**
 * Höhe der Leiste für die gegebenen Gruppen, wenn das Kapitel `offen` seine
 * Slides zeigt (oder keins, mit `offen = -1`).
 */
export function leistenHoehe(gruppen: ReadonlyArray<KapitelGruppe>, offen: number, m: PunktMasse): number {
    if (gruppen.length === 0) return 0;
    const kleine = offen >= 0 ? Math.max(0, (gruppen[offen]?.slides.length ?? 1) - 1) : 0;
    const anzahl = gruppen.length + kleine;
    return 2 * m.rand + gruppen.length * m.gross + kleine * m.klein + (anzahl - 1) * m.abstand;
}

/** Wo ein Punkt sitzt — gemessen von der Mitte der Leiste, nach unten positiv. */
export interface PunktLage {
    /** Slide-Index, zu dem der Punkt springt */
    slide: number;
    kapitel: number;
    /** 0 = großer Punkt (Kapitelanfang bzw. Slide ohne Gruppen), sonst Nummer im Kapitel */
    nr: number;
    y: number;
    /** Radius; 0 = eingeklappt, steckt im Kapitelpunkt */
    r: number;
}

/**
 * Lage aller Punkte, wenn Kapitel `offen` aufgeklappt ist (-1: keins) — auch
 * der eingeklappten: Sie sitzen mit Radius 0 im Kapitelpunkt, damit sie beim
 * Aufklappen aus ihm herausfließen und beim Zuklappen hineingesogen werden.
 */
export function punktLagen(
    gruppen: ReadonlyArray<KapitelGruppe>,
    offen: number,
    m: PunktMasse,
): { punkte: PunktLage[]; hoehe: number } {
    const punkte: PunktLage[] = [];
    let y = m.rand;
    gruppen.forEach((g, k) => {
        if (k > 0) y += m.abstand;
        const kapitelY = y + m.gross / 2;
        punkte.push({ slide: g.slides[0], kapitel: k, nr: 0, y: kapitelY, r: m.gross / 2 });
        y += m.gross;
        const auf = g.istKapitel && k === offen;
        g.slides.slice(1).forEach((slide, i) => {
            if (!auf) {
                punkte.push({ slide, kapitel: k, nr: i + 1, y: kapitelY, r: 0 });
                return;
            }
            y += m.abstand;
            punkte.push({ slide, kapitel: k, nr: i + 1, y: y + m.klein / 2, r: m.klein / 2 });
            y += m.klein;
        });
    });
    const hoehe = gruppen.length > 0 ? y + m.rand : 0;
    for (const p of punkte) p.y -= hoehe / 2;
    return { punkte, hoehe };
}

/**
 * Passende Maße für die verfügbare Höhe — oder null, wenn auch eng nicht alles
 * passt (dann zeigt der Player keine Leiste; die Nav-Knöpfe bleiben).
 */
export function passendeMasse(
    gruppen: ReadonlyArray<KapitelGruppe>,
    maxHoehe: number,
): PunktMasse | null {
    // Das größte Kapitel offen ist der ungünstigste Fall — passt der, springt
    // die Leiste beim Weiterlesen nie in eine andere Größe.
    let groesstes = -1;
    let laenge = 0;
    gruppen.forEach((g, i) => {
        if (g.slides.length > laenge) {
            laenge = g.slides.length;
            groesstes = i;
        }
    });
    for (const m of [PUNKT_MASSE.normal, PUNKT_MASSE.eng]) {
        if (leistenHoehe(gruppen, groesstes, m) <= maxHoehe) return m;
    }
    return null;
}

/**
 * WARNUNG für die Editoren (Jan 17.10.2026, „über 40 Slides gehen die Dots
 * weg"): Passt die Punkte-Leiste in diese Höhe? Dieselbe Rechnung wie die
 * Leiste selbst (passendeMasse, ungünstigster Fall = größtes Kapitel offen).
 * Ohne Punkte (weniger als zwei) gibt es nichts zu warnen.
 */
export function naviPunktePassen(
    slides: ReadonlyArray<KapitelSlide | null | undefined>,
    maxHoehe: number,
): boolean {
    const gruppen = kapitelGruppen(slides);
    return gruppen.length < 2 || slides.length < 3 || passendeMasse(gruppen, maxHoehe) !== null;
}

/** Punkte im ungünstigsten Fall: Kapitel-Punkte + die Slides des größten Kapitels. */
export function naviPunkteAnzahl(slides: ReadonlyArray<KapitelSlide | null | undefined>): number {
    const gruppen = kapitelGruppen(slides);
    const groesstes = Math.max(1, ...gruppen.map((g) => g.slides.length));
    return gruppen.length + (gruppen[0]?.istKapitel ? groesstes - 1 : 0);
}
